<template>
  <v-dialog v-model="dialogVisible" max-width="900" scrollable>
    <v-card v-if="task">
      <v-card-title class="text-h6 px-4 pt-4">
        {{ task.name }}
      </v-card-title>

      <v-card-text class="pa-0">
        <v-table v-if="task.subtaskProfiles?.length > 0" density="compact">
          <thead>
            <tr>
              <th class="text-left">Title</th>
              <th class="text-left">Template</th>
              <th class="text-center" style="width: 80px;">Weighting</th>
              <th class="text-left">Lead Team</th>
              <th class="text-center" style="width: 100px;">Changes</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(subtask, index) in task.subtaskProfiles"
              :key="index"
              :class="getSubtaskRowClass(subtask)"
            >
              <td>
                <div class="d-flex align-center">                
                  {{ subtask.title }}
                </div>
              </td>
              <td>
                {{ subtask.itemTemplateCode || '—' }}
              </td>
              <td class="text-center">
                {{ subtask.weighting ?? '—' }}
              </td>
              <td>
                {{ subtask.leadTeamName || subtask.defaultLeadByTeamId || '—' }}
              </td>
              <td class="text-center">
                <v-chip
                  v-if="subtask.changeDetails?.length > 0"
                  :color="getChipColorForType(subtask.changeType)"
                  size="small"
                  label
                  class="cursor-pointer"
                  @click="showChangeDetails(subtask)"
                >
                  {{ subtask.changeDetails.length }}
                </v-chip>
                <span v-else class="text-medium-emphasis">—</span>
              </td>
            </tr>
          </tbody>
        </v-table>
        <div v-else class="text-body-2 text-medium-emphasis pa-4 text-center">
          No subtasks for this task.
        </div>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="dialogVisible = false">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Change Details Dialog -->
    <v-dialog v-model="changeDetailsDialog" max-width="500">
      <v-card v-if="selectedSubtask">
        <v-card-title class="text-h6 px-4 pt-4">
          {{ selectedSubtask.title }}
        </v-card-title>
        <v-card-text>
          <v-list density="compact">
            <v-list-item
              v-for="(detail, idx) in selectedSubtask.changeDetails"
              :key="idx"
            >
              <v-list-item>{{ detail }}</v-list-item>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="changeDetailsDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { CHANGE_TYPES } from '@/utils/TaskSetProfileChangeDetector';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  task: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update:modelValue']);

const changeDetailsDialog = ref(false);
const selectedSubtask = ref(null);

// Debug logging
watch(() => props.task, (newTask) => {
  if (newTask) {
    console.log('SubtasksDialog - task received:', newTask.name);
    console.log('SubtasksDialog - subtaskProfiles:', JSON.stringify(newTask.subtaskProfiles, null, 2));
    if (newTask.subtaskProfiles?.length > 0) {
      newTask.subtaskProfiles.forEach((s, i) => {
        console.log(`SubtasksDialog - subtask[${i}] itemTemplateCode:`, s.itemTemplateCode, 'type:', typeof s.itemTemplateCode);
      });
    }
  }
}, { immediate: true });

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

function getChipColorForType(changeType) {
  switch (changeType) {
    case CHANGE_TYPES.ADDED:
      return 'success';
    case CHANGE_TYPES.MODIFIED:
      return 'warning';
    case CHANGE_TYPES.REMOVED:
      return 'error';
    default:
      return 'info';
  }
}

function getSubtaskRowClass(subtask) {
  switch (subtask.changeType) {
    case CHANGE_TYPES.ADDED:
      return 'bg-green-lighten-4';
    case CHANGE_TYPES.MODIFIED:
      return 'bg-yellow-lighten-4';
    case CHANGE_TYPES.REMOVED:
      return 'bg-red-lighten-4';
    default:
      return '';
  }
}

function showChangeDetails(subtask) {
  selectedSubtask.value = subtask;
  changeDetailsDialog.value = true;
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
